import axios, { AxiosInstance, AxiosResponse } from 'axios';
import config from '../config';
import {
  AuthResponse,
  User,
  StockData,
  OptionsChain,
  OptionContract,
  StrategyRecommendationResponse,
  BrowseAllContractsResponse,
  RiskProfile,
  CronJobLog,
  CronJobStats,
  AdminLogFilters,
  AdminLogsResponse,
  JobTypeInfo,
  CronBatchSummary,
  PositionAnalysisRequest,
  PositionAnalysisResponse,
  RollPositionRequest,
  RollPositionResponse,
  CompositionData
} from '../types';
import {
  DisciplineState,
  DisciplineThresholds,
  DisciplineThresholdsBundle,
  DisciplineThresholdsUpdate,
  DisciplinePreviewGridRequest,
  DisciplinePreviewGridResponse,
  Intent,
} from '../types/discipline';
import { PerformancePage, PerformancePeriod, PerformanceAggregation } from '../types/performance';
import { optionsCache } from './optionsCache';
import type { StockScreenerData } from '../components/screener/columnDefinitions';


export interface RollOption {
  strike: number;
  expiry: string;
  dte: number;
  extension: number;
  delta: number;
  sell_price: number | null;
  buyback_price: number | null;
  net_credit: number;
  net_credit_total: number;
  open_interest: number | null;
  crosses_earnings: boolean;
  above_basis: boolean;
  viable: boolean;
  reason: string | null;
  is_target: boolean;
}

export interface RollOptions {
  available: boolean;
  reason?: string;
  intent: 'hold' | 'wheel' | 'release';
  position_type: 'covered_call' | 'cash_secured_put';
  strike: number;
  expiry: string;
  contracts: number;
  buyback_price: number | null;
  stock_price: number | null;
  net_basis: number;
  next_earnings_date: string | null;
  max_roll_extension: number;
  min_roll_credit: number;
  rule_action: string;
  rule_name: string | null;
  alternative: { kind: 'buyback' | 'assign'; label: string; detail: string; cost_total?: number; pnl_total: number | null };
  options: RollOption[];
}

const API_BASE_URL = config.API_URL;

// Per-symbol cache for the (immutable) setup matrix — shared across card mounts
// so repeated opens never re-hit the rate-limited endpoint. See getStockSetupMatrix.
export type SetupMatrixCellFacts = {
  avg_pnl: number | null;        // fraction (older clients)
  pnl_pct: number | null;        // Setup P&L shown, % per 35 days; null under min_n cases
  n: number | null;
  share: number | null;          // % of the stock's measured days
  frequency: 'frequent' | 'occasional' | 'rare' | null;
  tone: 'losing' | 'well_above' | 'above' | 'below' | null;
};
export type SetupMatrixResponse = {
  symbol: string;
  baseline_pnl: number | null;   // usual result, %; null when unknown
  current_cell: string | null;
  current_share: number | null;
  current_frequency: 'frequent' | 'occasional' | 'rare' | null;
  cluster_id: number | null;
  min_n: number;
  cells: Record<string, SetupMatrixCellFacts>;
};
const _setupMatrixCache = new Map<string, Promise<SetupMatrixResponse>>();

/** Coach funnel metrics returned by GET /api/admin/coach/metrics. */
export interface CoachFunnel {
  reached: number;
  reached_reddit: number;
  chatted: number;
  questions: number;
  emailed: number;
  subscribed: number;
}
export interface CoachMetrics {
  as_of: string;
  window_days: number;
  primary_day: string;
  tracking_since: string | null;
  window: CoachFunnel;
  primary: CoachFunnel;
  cumulative: CoachFunnel;
  rates: {
    chat_rate: number | null;
    email_rate: number | null;
    reddit_share: number | null;
  };
  tiers: { free: number; pro: number; premium: number };
  by_day: Array<{
    day: string;
    reached: number;
    chatted: number;
    questions: number;
    emailed: number;
  }>;
  recent_questions: Array<{
    text: string;
    question_number: number | null;
    utm_source: string | null;
    created_at: string;
  }>;
  top_keywords: Array<{ term: string; n: number }>;
}

/** One ad source/campaign row within a surface's funnel. */
export interface DistributionCampaign {
  source: string;
  campaign: string;
  reached: number;
  engaged: number;
  leads: number;
  would_pay_yes: number;
  engaged_rate: number | null;
  lead_rate: number | null;
}
export interface DistributionSurface {
  surface: string;
  reached: number;
  engaged: number;
  leads: number;
  would_pay_yes: number;
  engaged_rate: number | null;
  lead_rate: number | null;
  by_campaign: DistributionCampaign[];
}
/** Surface-agnostic funnel from GET /api/admin/distribution/metrics. */
export interface DistributionMetrics {
  window_days: number;
  generated_at: string;
  totals: { reached: number; engaged: number; leads: number };
  surfaces: DistributionSurface[];
}

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
        // 402 = Pro feature hit by a Free user (paywall bridge, BAB-71). Surface
        // the upgrade modal globally instead of letting it bubble as a raw error.
        // UpgradeModal listens for this event; the original promise still rejects
        // so the calling component can stop its own loading/state.
        if (error.response?.status === 402) {
          const detail = error.response?.data?.detail;
          window.dispatchEvent(
            new CustomEvent('babylon:upgrade-required', {
              detail: typeof detail === 'object' && detail !== null ? detail : {},
            })
          );
        }
        return Promise.reject(error);
      }
    );
  }


  // Auth endpoints
  async login(username: string, password: string): Promise<AuthResponse> {
    const formData = new FormData();
    formData.append('username', username);
    formData.append('password', password);
    
    const response: AxiosResponse<AuthResponse> = await this.api.post('/api/auth/login', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  async register(userData: {
    email: string;
    password: string;
    username?: string;
    full_name?: string;
    start_pro_trial?: boolean;
  }): Promise<AuthResponse> {
    const response: AxiosResponse<AuthResponse> = await this.api.post('/api/auth/register', userData);
    return response.data;
  }

  async getCurrentUser(): Promise<User> {
    const response: AxiosResponse<User> = await this.api.get('/api/auth/me');
    return response.data;
  }

  async verifyEmail(token: string): Promise<AuthResponse> {
    const response: AxiosResponse<AuthResponse> = await this.api.post(
      '/api/auth/verify-email',
      { token }
    );
    return response.data;
  }

  async resendVerification(): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await this.api.post(
      '/api/auth/resend-verification'
    );
    return response.data;
  }

  async forgotPassword(email: string): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await this.api.post(
      '/api/auth/forgot-password',
      { email }
    );
    return response.data;
  }

  async resetPassword(token: string, password: string): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await this.api.post(
      '/api/auth/reset-password',
      { token, password }
    );
    return response.data;
  }

  // Stock endpoints
  async searchStocks(query: string): Promise<{ matches: string[] }> {
    const response = await this.api.get(`/api/stocks/search?q=${encodeURIComponent(query)}`);
    return response.data;
  }

  async getStockData(
    symbol: string,
    timespan: string = 'day',
    multiplier: number = 1,
    fromDate?: string,
    toDate?: string
  ): Promise<{ status: string; symbol: string; data: { results: StockData[] }; source?: string }> {
    let url = `/api/stocks/data/${symbol}?timespan=${timespan}&multiplier=${multiplier}`;
    if (fromDate) url += `&from_date=${fromDate}`;
    if (toDate) url += `&to_date=${toDate}`;

    const response = await this.api.get(url);
    return response.data;
  }

  async getStockDataBatch(
    symbols: string[],
    timespan: string = 'week',
    fromDate?: string,
    toDate?: string
  ): Promise<{ status: string; timespan: string; symbols: string[]; data: Record<string, { results: StockData[] }> }> {
    const symbolsParam = symbols.join(',');
    let url = `${API_BASE_URL}/api/stocks/data/batch?symbols=${symbolsParam}&timespan=${timespan}`;
    if (fromDate) url += `&from_date=${fromDate}`;
    if (toDate) url += `&to_date=${toDate}`;

    // Use fetch for granular timing (network vs JSON parsing)
    console.log(`🚀 [API] Starting batch request for ${symbols.length} symbols...`);
    const startRequest = performance.now();

    const token = localStorage.getItem('token');
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      }
    });
    const networkTime = performance.now() - startRequest;
    console.log(`📡 [API] Response received in ${networkTime.toFixed(0)}ms`);

    // Step 1: Download body as text (measures network transfer)
    const startDownload = performance.now();
    const text = await response.text();
    const downloadTime = performance.now() - startDownload;
    console.log(`📥 [API] Body downloaded in ${downloadTime.toFixed(0)}ms (${(text.length / 1024).toFixed(0)} KB)`);

    // Step 2: Parse JSON (measures CPU parsing time)
    const startParse = performance.now();
    const data = JSON.parse(text);
    const parseTime = performance.now() - startParse;
    console.log(`📦 [API] JSON parsed in ${parseTime.toFixed(0)}ms`);

    if (!response.ok) {
      throw new Error(data.detail || 'Batch request failed');
    }

    return data;
  }

  /**
   * Get chart data with full OHLCV + technical indicators for a single symbol
   * Used by fullscreen chart modal for candlestick charts with SMA/RSI/IV overlays
   */
  async getChartDataWithIndicators(
    symbol: string,
    fromDate?: string,
    toDate?: string,
    includeIV: boolean = true
  ): Promise<{
    status: string;
    symbol: string;
    source: string;
    include_indicators: boolean;
    include_iv: boolean;
    data: {
      results: Array<{
        symbol: string;
        timestamp: number;
        open: number | null;
        high: number | null;
        low: number | null;
        close: number | null;
        volume: number | null;
        vwap: number | null;
        sma_50: number | null;
        sma_200: number | null;
        rsi_14: number | null;
      }>;
      iv_data?: Array<{
        timestamp: number;
        avg_iv: number | null;
      }>;
    };
  }> {
    let url = `${API_BASE_URL}/api/stocks/data/${symbol}?include_indicators=true`;
    if (includeIV) url += `&include_iv=true`;
    if (fromDate) url += `&from_date=${fromDate}`;
    if (toDate) url += `&to_date=${toDate}`;

    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch chart data for ${symbol}`);
    }

    return response.json();
  }

  async getStockSetupMatrix(symbol: string): Promise<SetupMatrixResponse> {
    // The setup matrix is behavioral-cluster data — immutable per symbol within a
    // session. Cache the in-flight promise so repeated opens / card remounts share
    // one request (guards against request storms against the rate limiter). Failed
    // requests are evicted so they can be retried.
    const key = symbol.toUpperCase();
    const cached = _setupMatrixCache.get(key);
    if (cached) return cached;
    const request = this.api
      .get(`/api/stocks/${encodeURIComponent(symbol)}/setup-matrix`)
      .then((response) => response.data)
      .catch((err) => {
        _setupMatrixCache.delete(key);
        throw err;
      });
    _setupMatrixCache.set(key, request);
    return request;
  }

  async getStockScreenerData(
    limit: number = 50,
    offset: number = 0,
    forceRefresh: boolean = false,
    sortBy: string = 'cc_score',
    sortOrder: string = 'desc',
    filters?: {
      search?: string;
      max_price?: number;
      min_iv_rank?: number;
      max_iv_rank?: number;
      min_rsi?: number;
      max_rsi?: number;
      min_net_margin?: number;
      max_net_margin?: number;
      max_debt_equity?: number;
      trend_filter?: string;
      max_spread_pct?: number;
      min_spread_pct?: number;
      sectors?: string;
      market_cap_categories?: string;
      min_annualized_yield?: number;
      max_annualized_yield?: number;
      min_dividend_yield?: number;
      max_dividend_yield?: number;
      earnings_within_days?: number;
      exclude_earnings_within_days?: number;
      min_slope_12w?: number;
      max_slope_12w?: number;
      macd_signals?: string;
      min_open_interest?: number;
      max_open_interest?: number;
      min_baseline_pnl?: number;
      max_baseline_pnl?: number;
      min_dimension1_stars?: number;
      min_dimension2_stars?: number;
      setup_signals?: string;
      profile?: 'growth' | 'value' | 'income';
      framework_id?: number;
      strategy?: 'cc' | 'csp';
    }
  ): Promise<{
    status: string;
    stocks: StockScreenerData[];
    total: number | null;
    strategy?: 'cc' | 'csp';
    source?: string;
    last_updated: string | null;
    liquidity_rule?: { version: 'v1' | 'v2'; max_spread_pct: number; min_open_interest: number; min_stock_volume: number };
    framework_id?: number;
    framework_name?: string;
  }> {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
      sort_by: sortBy,
      sort_order: sortOrder
    });
    if (forceRefresh) {
      params.append('force_refresh', 'true');
    }

    // Add filter parameters if provided
    // Note: Use != null to catch both null and undefined values
    if (filters) {
      if (filters.search) {
        params.append('search', filters.search);
      }
      if (filters.max_price != null) {
        params.append('max_price', filters.max_price.toString());
      }
      if (filters.min_iv_rank != null) {
        params.append('min_iv_rank', filters.min_iv_rank.toString());
      }
      if (filters.max_iv_rank != null) {
        params.append('max_iv_rank', filters.max_iv_rank.toString());
      }
      if (filters.min_rsi != null) {
        params.append('min_rsi', filters.min_rsi.toString());
      }
      if (filters.max_rsi != null) {
        params.append('max_rsi', filters.max_rsi.toString());
      }
      if (filters.min_net_margin != null) {
        params.append('min_net_margin', filters.min_net_margin.toString());
      }
      if (filters.max_debt_equity != null) {
        params.append('max_debt_equity', filters.max_debt_equity.toString());
      }
      if (filters.trend_filter) {
        params.append('trend_filter', filters.trend_filter);
      }
      if (filters.max_spread_pct != null) {
        params.append('max_spread_pct', filters.max_spread_pct.toString());
      }
      if (filters.min_spread_pct != null) {
        params.append('min_spread_pct', filters.min_spread_pct.toString());
      }
      if (filters.max_net_margin != null) {
        params.append('max_net_margin', filters.max_net_margin.toString());
      }
      if (filters.sectors) {
        params.append('sectors', filters.sectors);
      }
      if (filters.market_cap_categories) {
        params.append('market_cap_categories', filters.market_cap_categories);
      }
      if (filters.min_annualized_yield != null) {
        params.append('min_annualized_yield', filters.min_annualized_yield.toString());
      }
      if (filters.max_annualized_yield != null) {
        params.append('max_annualized_yield', filters.max_annualized_yield.toString());
      }
      if (filters.min_dividend_yield != null) {
        params.append('min_dividend_yield', filters.min_dividend_yield.toString());
      }
      if (filters.max_dividend_yield != null) {
        params.append('max_dividend_yield', filters.max_dividend_yield.toString());
      }
      if (filters.earnings_within_days != null) {
        params.append('earnings_within_days', filters.earnings_within_days.toString());
      }
      if (filters.exclude_earnings_within_days != null) {
        params.append('exclude_earnings_within_days', filters.exclude_earnings_within_days.toString());
      }
      if (filters.min_slope_12w != null) {
        params.append('min_slope_12w', filters.min_slope_12w.toString());
      }
      if (filters.max_slope_12w != null) {
        params.append('max_slope_12w', filters.max_slope_12w.toString());
      }
      if (filters.macd_signals) {
        params.append('macd_signals', filters.macd_signals);
      }
      if (filters.min_open_interest != null) {
        params.append('min_open_interest', filters.min_open_interest.toString());
      }
      if (filters.max_open_interest != null) {
        params.append('max_open_interest', filters.max_open_interest.toString());
      }
      if (filters.min_baseline_pnl != null) {
        params.append('min_baseline_pnl', filters.min_baseline_pnl.toString());
      }
      if (filters.max_baseline_pnl != null) {
        params.append('max_baseline_pnl', filters.max_baseline_pnl.toString());
      }
      if (filters.min_dimension1_stars != null) {
        params.append('min_dimension1_stars', filters.min_dimension1_stars.toString());
      }
      if (filters.min_dimension2_stars != null) {
        params.append('min_dimension2_stars', filters.min_dimension2_stars.toString());
      }
      if (filters.setup_signals) {
        params.append('setup_signals', filters.setup_signals);
      }
      if (filters.profile) {
        params.append('profile', filters.profile);
      }
      if (filters.framework_id != null) {
        params.append('framework_id', filters.framework_id.toString());
      }
      if (filters.strategy) {
        params.append('strategy', filters.strategy);
      }
    }

    // Gold phase 2b: every figure arrives computed for the requested lens (CC / CSP);
    // null stays null (shown "—"). No defaults, no remapping here.
    const response = await this.api.get(`/api/stocks/screener?${params.toString()}`);
    return {
      status: response.data.status,
      stocks: response.data.stocks ?? [],
      total: response.data.total ?? null,
      strategy: response.data.strategy,
      source: response.data.source,
      last_updated: response.data.last_updated ?? null,
      liquidity_rule: response.data.liquidity_rule,
      framework_id: response.data.framework_id,
      framework_name: response.data.framework_name,
    };
  }

  async getSymbolsWithOptions(search?: string, limit: number = 50): Promise<{
    status: string;
    symbols: Array<{ symbol: string; name: string; has_options?: boolean }>;
    count: number;
    source: string;
  }> {
    let url = '/api/stocks/symbols/with-options';
    const params = new URLSearchParams();
    
    if (search) {
      params.append('search', search);
    }
    params.append('limit', limit.toString());
    
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
    
    const response = await this.api.get(url);
    return response.data;
  }

  // Market data endpoints
  async getVixPercentile(): Promise<{
    status: string;
    current_vix?: number;
    week_52_high?: number;
    week_52_low?: number;
    percentile?: number;
    regime?: 'low_fear' | 'moderate' | 'elevated' | 'high_fear';
    data_points?: number;
    message?: string;
  }> {
    const response = await this.api.get('/api/stocks/market/vix-percentile');
    return response.data;
  }

  async getSpyTrend(): Promise<{
    status: string;
    symbol?: string;
    current_price?: number;
    sma_200?: number;
    trend?: 'bullish' | 'bearish' | 'neutral';
    percent_above_sma?: number;
    sma_200_slope?: number | null;
    slope_direction?: 'up' | 'down' | 'flat' | null;
    message?: string;
  }> {
    const response = await this.api.get('/api/stocks/market/spy-trend');
    return response.data;
  }

  async getSectorPerformance(period: string = '1D'): Promise<{
    status: string;
    period: string;
    sectors: Array<{ sector: string; symbol: string; change_percent: number }>;
  }> {
    const response = await this.api.get(`/api/stocks/market/sector-performance?period=${period}`);
    return response.data;
  }

  async getSetupSignalDistribution(): Promise<{
    status: string;
    total: number;
    thresholds: { strong: number; positive: number; marginal: number };
    signals: {
      strong: number;
      positive: number;
      marginal: number;
      losing: number;
      unknown: number;
    };
  }> {
    const response = await this.api.get('/api/stocks/market/setup-signals');
    return response.data;
  }

  async getMarketStatus(): Promise<{
    status: string;
    date: string;
    market_status: 'open' | 'closed' | 'early_close';
    is_trading_day: boolean;
    holiday_name: string | null;
    open_time: string | null;
    close_time: string | null;
    last_trading_day: string | null;
  }> {
    const response = await this.api.get('/api/stocks/market/status');
    return response.data;
  }

  // Opportunity Scanner endpoint
  async getOpportunities(
    limit: number = 10
  ): Promise<{
    status: string;
    title: string;
    stocks: Array<{
      symbol: string;
      company_name: string | null;
      current_price: number | null;
      iv_rank: number | null;
      best_ann_yield: number | null;   // % per year, best contract
      best_dte: number | null;
      score: number | null;
      quality_score: number | null;    // Income fit
      timing_score: number | null;
      passes_defaults: boolean | null;
      days_to_earnings: number | null;
      liquidity?: { pass: boolean; reasons: string[] };
    }>;
    total_available: number;
  }> {
    const response = await this.api.get(`/api/stocks/opportunities?limit=${limit}`);
    return response.data;
  }

  // Options endpoints
  async getOptionsChain(symbol: string, expirationDate?: string): Promise<OptionsChain> {
    let url = `/api/options/chain/${symbol}`;
    if (expirationDate) {
      url += `?expiration_date=${expirationDate}`;
    }

    const response: AxiosResponse<OptionsChain> = await this.api.get(url);
    return response.data;
  }

  async getBulkOptions(
    symbol: string,
    minDays: number = 4,
    maxDays: number = 90,
    minDelta: number = 0.1,
    maxDelta: number = 0.5,
    contractType: 'call' | 'put' = 'call'  // BAB-28: 'put' for CSP/wheel
  ): Promise<{
    status: string;
    symbol: string;
    current_price: number;
    expirations: Array<{
      date: string;
      days_to_expiration: number;
      contracts: OptionContract[];
    }>;
    total_contracts: number;
    cache_timestamp: string;
  }> {
    const params = new URLSearchParams({
      min_days: minDays.toString(),
      max_days: maxDays.toString(),
      min_delta: minDelta.toString(),
      max_delta: maxDelta.toString(),
      contract_type: contractType
    });

    const response = await this.api.get(`/api/options/bulk/${symbol}?${params.toString()}`);
    return response.data;
  }

  async getOptionsCached(
    symbol: string,
    expirationDate?: string,
    useCache: boolean = true
  ): Promise<OptionsChain> {
    // If cache is disabled or no expiration specified, use traditional API
    if (!useCache || !expirationDate) {
      return this.getOptionsChain(symbol, expirationDate);
    }

    // Check cache first
    if (optionsCache.has(symbol)) {
      const contracts = optionsCache.getFilteredContracts(symbol, expirationDate);
      const currentPrice = optionsCache.getCurrentPrice(symbol);

      if (contracts.length > 0) {
        console.log(`📦 Using cached options for ${symbol}:${expirationDate}`, {
          contracts: contracts.length
        });

        return {
          status: 'success',
          symbol,
          current_price: currentPrice,
          expiration_date: expirationDate,
          options: contracts,
          source: 'cache'
        };
      }
    }

    // Cache miss or no data - fallback to traditional API
    console.log(`🌐 Cache miss for ${symbol}:${expirationDate}, falling back to API`);
    return this.getOptionsChain(symbol, expirationDate);
  }

  // Portfolio endpoints
  async getPortfolioPositions(status?: 'active' | 'closed'): Promise<{
    positions: Array<{
      id: string;
      user_id: number;
      ticker: string;
      entry_date: string;
      expiry_date: string;
      strike_price: number;
      entry_premium: number;
      entry_price: number;
      created_at: string;
      updated_at: string;
      // Action tracking fields
      status?: string;
      last_action?: string;
      action_date?: string;
      action_notes?: string;
      close_price?: number;
      close_date?: string;
      // Enriched fields (may be present)
      current_price?: number;
      current_premium?: number;
      delta?: number;
      days_to_expiration?: number;
      implied_volatility?: number;
      stock_pnl?: number;
      option_pnl?: number;
      cc_pnl?: number;
      cc_pnl_percent?: number;
      unrealized_pnl?: number;
      // Gold v2 phase 3b: every gold.v_position column under its glossary name
      [goldField: string]: any;
    }>;
    total: number;
    snapshot_as_of?: string;
    /** status=active: headers by lens (gold.portfolio_current), each = sum of its rows. */
    summary?: Partial<Record<'all' | 'cc' | 'csp', import('../types/position').PortfolioSummary>> | null;
    /** status=active: shares assigned from a put awaiting "still holding?". */
    assigned_shares?: import('../types/position').AssignedShares[] | null;
  }> {
    let url = '/api/portfolio/positions';
    if (status) {
      url += `?status=${status}`;
    }
    const response = await this.api.get(url);
    return response.data;
  }

  async createPortfolioPosition(position: {
    id: string;
    ticker: string;
    /** Omit: the server records the New York trade date (never the browser's UTC date). */
    entry_date?: string;
    expiry_date: string;
    strike_price: number;
    entry_premium: number;
    entry_price: number;
    num_contracts?: number;
    framework_id?: number;
    position_type?: 'covered_call' | 'cash_secured_put';  // BAB-28
    intent?: 'hold' | 'wheel' | 'release';  // BAB-71: Discipline intent chosen at open
  }): Promise<{
    id: string;
    user_id: number;
    ticker: string;
    entry_date: string;
    expiry_date: string;
    strike_price: number;
    entry_premium: number;
    entry_price: number;
    num_contracts: number;
    created_at: string;
    updated_at: string;
  }> {
    const response = await this.api.post('/api/portfolio/positions', position);
    return response.data;
  }

  /** Glossary assigned_shares_confirmation: "still holding?" Yes, or Sold + price (+ NY date). */
  async confirmAssignedShares(
    campaignId: string,
    body: { still_holding: boolean; sold_price?: number; sold_date?: string }
  ): Promise<any> {
    const response = await this.api.post(`/api/portfolio/campaigns/${campaignId}/holding`, body);
    return response.data;
  }

  /** Close dialog preview at the user's fill (same definitions as the gold row). */
  async getClosePreview(positionId: string, fill: number): Promise<import('../types/position').ClosePreview> {
    const response = await this.api.get(`/api/portfolio/positions/${positionId}/close-preview`, { params: { fill } });
    return response.data;
  }

  async deletePortfolioPosition(positionId: string): Promise<{ message: string }> {
    const response = await this.api.delete(`/api/portfolio/positions/${positionId}`);
    return response.data;
  }

  async recordPositionAction(
    positionId: string,
    action: string,
    notes?: string,
    metricsSnapshot?: any
  ): Promise<{ message: string; position_id: string }> {
    const response = await this.api.post(`/api/portfolio/positions/${positionId}/action`, {
      action,
      notes,
      metrics_snapshot: metricsSnapshot
    });
    return response.data;
  }

  /**
   * BAB-67 — Per-position Real-Yield P&L breakdown.
   * Returns the 6-bucket exhaustive + disjoint decomposition grouped into
   * Real / At-Risk / Total, plus the staleness confidence band and freshness
   * metadata. Math spec: `backend/docs/REAL_YIELD_SPEC.md` §1.1 + §3.
   * UI spec:  `backend/docs/REAL_YIELD_CARD_UX_SPEC.md`.
   */
  async getPositionPnLBreakdown(
    positionId: string
  ): Promise<import('../types/pnlBreakdown').PnLBreakdown> {
    const response = await this.api.get(
      `/api/portfolio/positions/${positionId}/pnl_breakdown`
    );
    return response.data;
  }

  async getPositionValueHistory(positionId: string): Promise<{
    position_id: string;
    ticker: string;
    snapshots: Array<{
      id: number;
      position_id: string;
      user_id: number;
      snapshot_date: string;
      underlying_price: number;
      option_price: number;
      underlying_value: number;
      option_value: number;
      stock_pnl_cumulative: number;
      option_pnl_cumulative: number;
      total_pnl_cumulative: number;
      capped_value_pnl_daily?: number;
      days_to_expiration: number;
      created_at: string;
    }>;
    total_snapshots: number;
  }> {
    const response = await this.api.get(`/api/portfolio/positions/${positionId}/value-history`);
    return response.data;
  }

  /**
   * BAB-60: flip the Discipline intent ('hold' | 'wheel' | 'release').
   * Evolved from BAB-47's 2-value toggle to the 3-way Discipline enum.
   * The card's State + reasoning lines all change on this flip, so the
   * UI should refetch discipline state on success.
   */
  async updatePositionIntent(positionId: string, intent: 'hold' | 'wheel' | 'release'): Promise<{
    message: string;
    position_id: string;
    intent: string;
    keep_shares_preference: boolean;
    /** BAB-66 race fix: server returns the fresh Discipline verdict post-commit
     *  so callers consume it directly instead of firing a separate GET that
     *  races the PATCH commit (and reads the OLD intent). */
    discipline: DisciplineState;
  }> {
    const response = await this.api.patch(`/api/portfolio/positions/${positionId}/intent`, {
      intent,
    });
    return response.data;
  }

  /**
   * BAB-60: flip the `thesis_broken` factual flag. UI: "I no longer want
   * to own this stock." TRUE triggers a tier-1 hard stop in the engine.
   */
  async updatePositionThesisBroken(positionId: string, thesisBroken: boolean): Promise<{
    message: string;
    position_id: string;
    thesis_broken: boolean;
    /** BAB-66 race fix: same authoritative-verdict pattern as PATCH /intent. */
    discipline: DisciplineState;
  }> {
    const response = await this.api.patch(`/api/portfolio/positions/${positionId}/thesis-broken`, {
      thesis_broken: thesisBroken,
    });
    return response.data;
  }

  /**
   * BAB-60: fetch the Discipline engine output for a single position.
   * Drives the card's State + always-visible reasoning lines (Rule / Flip).
   * Pure read; safe to poll on intent change.
   */
  async getPositionDiscipline(positionId: string): Promise<DisciplineState> {
    const response = await this.api.get(`/api/portfolio/positions/${positionId}/discipline`);
    return response.data;
  }

  /**
   * The Discipline verdict for a position under each of the three intents,
   * read-only (nothing is saved). Powers the cockpit's intent comparison.
   */
  async getPositionDisciplineByIntent(positionId: string): Promise<import('../types/discipline').DisciplineByIntent> {
    const response = await this.api.get(`/api/portfolio/positions/${positionId}/discipline/intents`);
    return response.data;
  }

  /**
   * BAB-61: load all three intents' threshold sets at once. Settings →
   * Frameworks → 3 Discipline tabs hydrates from this single call.
   * Returns spec defaults for any intent the user hasn't customized.
   */
  async getDisciplineThresholds(): Promise<DisciplineThresholdsBundle> {
    const response = await this.api.get('/api/portfolio/discipline-thresholds');
    return response.data;
  }

  /** Latest delayed price per symbol (glossary stock_price_live; never stored). */
  async getLatestPrices(symbols: string[]): Promise<import('../types/latestPrice').LatestStockQuotesResponse> {
    const response = await this.api.get('/api/stocks/latest-prices', { params: { symbols: symbols.join(',') } });
    return response.data;
  }

  /** Latest stock P&L / Today's P&L of open positions on the latest delayed price. */
  async getLatestPositionPnl(): Promise<import('../types/latestPrice').LatestPositionsResponse> {
    const response = await this.api.get('/api/portfolio/positions/latest');
    return response.data;
  }

  /**
   * BAB-61: save (upsert) thresholds for one intent. Server validates
   * against the hard rails (spec §2.1); out-of-range returns 422.
   */
  async updateDisciplineThresholds(intent: Intent, body: DisciplineThresholdsUpdate): Promise<DisciplineThresholds> {
    const response = await this.api.put(`/api/portfolio/discipline-thresholds/${intent}`, body);
    return response.data;
  }

  /**
   * BAB-64: 2D decision-map for the Settings → Frameworks → Discipline tab.
   * Returns the action the engine would recommend at each (stock_price, DTE)
   * cell of a hard-coded worked-example position.
   *
   * Stateless per (intent, strategy, thresholds, grid_size) — safe to call
   * on every slider drag (debounce on the caller).
   */
  async getDisciplinePreviewGrid(body: DisciplinePreviewGridRequest): Promise<DisciplinePreviewGridResponse> {
    const response = await this.api.post('/api/portfolio/discipline/preview-grid', body);
    return response.data;
  }

  /**
   * Roll a position to a new contract in a single atomic operation.
   * Preserves position ID and accumulates leg history for campaign tracking.
   */
  async rollPosition(
    positionId: string,
    request: RollPositionRequest
  ): Promise<RollPositionResponse> {
    const response = await this.api.post(
      `/api/portfolio/positions/${positionId}/roll`,
      {
        buyback_premium: request.buybackPremium,
        new_expiry_date: request.newExpiryDate,
        new_strike_price: request.newStrikePrice,
        new_premium: request.newPremium,
        current_stock_price: request.currentStockPrice,
        notes: request.notes
      }
    );
    return response.data;
  }

  // Roll order panel: the engine's roll target + ranked alternatives (same provider
  // and viability rules), the intent's alternative to rolling, and check inputs.
  async getRollOptions(positionId: string): Promise<RollOptions> {
    const response = await this.api.get(`/api/portfolio/positions/${positionId}/roll-options`);
    return response.data;
  }

  // Undo a roll made in the last 15 minutes (positionId = the NEW leg).
  async undoRoll(positionId: string): Promise<{ status: string; position_id: string }> {
    const response = await this.api.post(`/api/portfolio/positions/${positionId}/undo-roll`);
    return response.data;
  }

  // Wheel (BAB-16): continue an assigned cash-secured put into a covered call on the
  // assigned shares, within the same campaign (carries the assignment strike as cost basis).
  async continueAsCoveredCall(
    positionId: string,
    request: { newStrikePrice: number; newExpiryDate: string; newPremium: number; currentStockPrice?: number; notes?: string }
  ): Promise<{
    position_id: string;
    campaign_id: string;
    ticker: string;
    leg_number: number;
    assignment_strike: number;
    effective_cost_basis: number;
    net_premiums_per_share: number;
    new_strike_price: number;
    new_expiry_date: string;
    new_premium: number;
    message: string;
  }> {
    const response = await this.api.post(
      `/api/portfolio/positions/${positionId}/continue-as-cc`,
      {
        new_strike_price: request.newStrikePrice,
        new_expiry_date: request.newExpiryDate,
        new_premium: request.newPremium,
        current_stock_price: request.currentStockPrice,
        notes: request.notes
      }
    );
    return response.data;
  }

  // BAB-77: getPositionSentiment / savePositionSentiment (PME v2 outlook
  // capture) removed — their only consumer (RiskAssessmentPanel →
  // SentimentSelector) is deleted. Backend endpoint removal is BAB-79.

  // BAB-77: resolveDefenseState (POST /positions/{id}/resolve, PME v2) removed
  // from the client — the Manage modal is now a view of the card's
  // DisciplineState and makes no engine calls. Backend removal is BAB-79.

  async getUserPreferences(): Promise<{
    id: number;
    user_id: number;
    owned_stocks?: string[] | null;
    income_method?: string | null;
    share_goal?: string | null;
    company_type_preference?: string | null;
    risk_comfort?: string | null;
    [key: string]: any; // Allow other preference fields
  }> {
    const response = await this.api.get('/api/portfolio/preferences');
    return response.data;
  }

  async saveUserPreferences(preferences: {
    owned_stocks?: string[] | null;
    income_method?: string | null;
    share_goal?: string | null;
    company_type_preference?: string | null;
    risk_comfort?: string | null;
    [key: string]: any; // Allow other preference fields
  }): Promise<{
    id: number;
    user_id: number;
    owned_stocks?: string[] | null;
    income_method?: string | null;
    share_goal?: string | null;
    company_type_preference?: string | null;
    risk_comfort?: string | null;
    [key: string]: any;
  }> {
    // BAB-47 prefs-UI cleanup: keep_shares_preference was promoted from User-
    // level to per-position (BAB-45). The seed remains on the User row for the
    // onboarding moment only — post-onboarding writes via this generic prefs
    // endpoint must NOT mutate it (a portfolio-wide override is exactly the
    // wrong mental model now that intent is per-position). The open
    // `[key: string]: any` index would silently let a future caller slip the
    // field through; strip it defensively here.
    const { keep_shares_preference: _stripped, ...safe } = preferences;
    if (_stripped !== undefined) {
      // Loud warning rather than silent strip — surfaces in dev console if a
      // new code path tries to write the field via this endpoint.
      // eslint-disable-next-line no-console
      console.warn(
        '[BAB-47] Stripped keep_shares_preference from saveUserPreferences payload. ' +
          'Per-position intent lives on portfolio_positions; use updatePositionIntent(positionId, value) instead.'
      );
    }
    const response = await this.api.put('/api/portfolio/preferences', safe);
    return response.data;
  }

  /**
   * The Performance page (gold v2 phase 4b): every figure computed by the
   * backend from gold (types/performance.ts). Fractions, not percent; null = unknown.
   */
  async getPerformanceSummary(
    timePeriod: PerformancePeriod = 'YTD',
    aggregation: PerformanceAggregation = 'monthly',
    strategy?: 'all' | 'cc' | 'csp'
  ): Promise<PerformancePage> {
    // CC/CSP portfolio lens — omit for 'all' so the backend returns the whole book.
    const strategyParam = strategy && strategy !== 'all' ? `&strategy=${strategy}` : '';
    const response = await this.api.get(
      `/api/portfolio/performance-summary?time_period=${timePeriod}&aggregation=${aggregation}${strategyParam}`
    );
    return response.data;
  }

  /**
   * Stream the AI coach reply via SSE. The backend persists the conversation
   * and auto-injects the user's active positions into the system prompt every turn.
   *
   * Pass `conversationId` to append the turn to a specific thread (e.g. when
   * continuing a Strategy Review discussion).
   */
  async streamAICoachChat(
    message: string,
    onChunk: (text: string) => void,
    onDone: () => void,
    onError: (error: string) => void,
    conversationId?: number,
    /** Start a fresh server thread (blank chat page); its id arrives via onConversation. */
    newConversation?: boolean,
    onConversation?: (id: number) => void,
  ): Promise<void> {
    const token = localStorage.getItem('token');
    const params = new URLSearchParams();
    if (conversationId !== undefined) params.set('conversation_id', String(conversationId));
    else if (newConversation) params.set('new_conversation', 'true');
    const qs = params.toString() ? `?${params.toString()}` : '';
    let response: Response;
    try {
      response = await fetch(`${config.API_URL}/api/ai-coach/chat/stream${qs}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ message }),
      });
    } catch (e: any) {
      onError(e?.message || 'Network error');
      return;
    }

    if (!response.ok) {
      const err = await response.text();
      onError(err || `Request failed (${response.status})`);
      return;
    }

    const reader = response.body?.getReader();
    if (!reader) {
      onError('No response body');
      return;
    }

    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        try {
          const event = JSON.parse(line.slice(6));
          if (event.type === 'text') {
            onChunk(event.content);
          } else if (event.type === 'conversation') {
            if (typeof event.id === 'number') onConversation?.(event.id);
          } else if (event.type === 'done') {
            onDone();
          } else if (event.type === 'error') {
            onError(event.content);
          }
        } catch {
          // ignore malformed SSE lines
        }
      }
    }
  }

  /**
   * Stream a one-shot AI stock analysis for the screener card.
   */
  async streamStockAnalysis(
    symbol: string,
    stockData: Record<string, any>,
    onChunk: (text: string) => void,
    onDone: () => void,
    onError: (error: string) => void,
  ): Promise<void> {
    const token = localStorage.getItem('token');
    let response: Response;
    try {
      response = await fetch(`${config.API_URL}/api/ai-coach/analyze-stock/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ symbol, stock_data: stockData }),
      });
    } catch (e: any) {
      onError(e?.message || 'Network error');
      return;
    }

    if (!response.ok) {
      const err = await response.text();
      onError(err || `Request failed (${response.status})`);
      return;
    }

    const reader = response.body?.getReader();
    if (!reader) { onError('No response body'); return; }

    const decoder = new TextDecoder();
    let buffer = '';
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        try {
          const event = JSON.parse(line.slice(6));
          if (event.type === 'text') onChunk(event.content);
          else if (event.type === 'done') onDone();
          else if (event.type === 'error') onError(event.content);
        } catch { /* ignore malformed SSE */ }
      }
    }
  }

  /**
   * Analyze a covered call position and get AI recommendation.
   * One-shot analysis without conversation history.
   */
  async analyzePosition(positionData: PositionAnalysisRequest): Promise<PositionAnalysisResponse> {
    const response = await this.api.post('/api/ai-coach/analyze-position', positionData);
    return response.data;
  }

  async streamPortfolioBrief(
    forceRefresh: boolean = false,
    onChunk: (text: string) => void,
    onDone: (cached: boolean) => void,
    onError: (error: string) => void,
  ): Promise<void> {
    const token = localStorage.getItem('token');
    const response = await fetch(
      `${config.API_URL}/api/ai-coach/portfolio-brief/stream?force_refresh=${forceRefresh}`,
      {
        method: 'POST',
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
      }
    );

    if (!response.ok) {
      const err = await response.text();
      onError(err || 'Failed to stream brief');
      return;
    }

    const reader = response.body?.getReader();
    if (!reader) {
      onError('No response body');
      return;
    }

    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        try {
          const event = JSON.parse(line.slice(6));
          if (event.type === 'text') {
            onChunk(event.content);
          } else if (event.type === 'done') {
            onDone(event.cached ?? false);
          } else if (event.type === 'error') {
            onError(event.content);
          }
        } catch {
          // ignore malformed SSE lines
        }
      }
    }
  }

  /** Strategy review: the backend reads the figures from gold itself (phase 4b). */
  async streamStrategyReview(
    timePeriod: string,
    strategy: 'all' | 'cc' | 'csp',
    forceRefresh: boolean = false,
    onChunk: (text: string) => void,
    onDone: (meta: { cached: boolean; conversationId?: number }) => void,
    onError: (error: string) => void,
  ): Promise<void> {
    const token = localStorage.getItem('token');
    const strategyParam = strategy !== 'all' ? `&strategy=${strategy}` : '';
    const response = await fetch(
      `${config.API_URL}/api/ai-coach/strategy-review/stream?force_refresh=${forceRefresh}&time_period=${timePeriod}${strategyParam}`,
      {
        method: 'POST',
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
      }
    );

    if (!response.ok) {
      const err = await response.text();
      onError(err || 'Failed to stream strategy review');
      return;
    }

    const reader = response.body?.getReader();
    if (!reader) {
      onError('No response body');
      return;
    }

    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        try {
          const event = JSON.parse(line.slice(6));
          if (event.type === 'text') {
            onChunk(event.content);
          } else if (event.type === 'done') {
            onDone({
              cached: event.cached ?? false,
              conversationId: event.conversation_id,
            });
          } else if (event.type === 'error') {
            onError(event.content);
          }
        } catch {
          // ignore malformed SSE lines
        }
      }
    }
  }

  /**
   * Fetch a full AI Coach conversation (chronological message history) for
   * the current user. Used when hydrating the AI Coach page from a handoff
   * (e.g. clicking "Continue in AI Coach" inside the Strategy Review modal).
   */
  async getAICoachConversation(conversationId: number): Promise<{
    id: number;
    title: string | null;
    created_at: string | null;
    updated_at: string | null;
    messages: Array<{
      id: number;
      role: 'user' | 'assistant';
      content: string;
      created_at: string | null;
      metadata: Record<string, any> | null;
    }>;
  }> {
    const response = await this.api.get(`/api/ai-coach/conversations/${conversationId}`);
    return response.data;
  }

  // =============================================================================
  // Market Weather Strategy Recommendations
  // =============================================================================

  /**
   * Get Market Weather-based strategy recommendations for a symbol.
   *
   * Returns 3 strategy tiers:
   * - Defensive (Safety Focus): Lower delta, lower assignment risk
   * - Recommended (Balanced): Matrix-optimized balance of premium and safety
   * - Income (Yield Focus): Higher delta, higher premium
   *
   * Zone-based availability:
   * - Golden Zone: All strategies available
   * - Standard Zone: All strategies available
   * - Caution Zone: Income strategy disabled
   * - Dead Zone: No strategies available
   */
  async getStrategyRecommendations(
    symbol: string,
    riskProfile: RiskProfile = 'moderate',
    contractType: 'call' | 'put' = 'call'  // BAB-28: 'put' for CSP/wheel
  ): Promise<StrategyRecommendationResponse> {
    const response = await this.api.post(
      `/api/options/strategy-recommendations/${symbol}?contract_type=${contractType}`,
      {
        ticker: symbol,
        user_risk_profile: riskProfile
      }
    );
    return response.data;
  }

  /**
   * Get all available contracts for "Browse All" feature.
   * Returns all valid OTM contracts (calls by default, puts for CSP) with pre-computed metrics.
   */
  async browseAllContracts(
    symbol: string,
    contractType: 'call' | 'put' = 'call'  // BAB-28
  ): Promise<BrowseAllContractsResponse> {
    const response = await this.api.get(
      `/api/options/strategy-recommendations/${symbol}/browse?contract_type=${contractType}`
    );
    return response.data;
  }

  /**
   * Get roll strategy contracts for position rolling.
   * Returns 3 contracts selected by strategy (Defensive/Balanced/Income):
   * - Defensive: delta ~0.15
   * - Balanced: delta ~0.25
   * - Income: delta ~0.35
   */
  async getRollStrategies(
    symbol: string,
    currentExpiry: string,
    costToClose: number,
    contractType: 'call' | 'put' = 'call'  // BAB-31: 'put' rolls a CSP
  ): Promise<{
    status: string;
    symbol: string;
    current_price: number;
    cost_to_close: number;
    min_expiry_date: string;
    strategies: Array<{
      strategy_type: string;
      strategy_title: string;
      target_delta: number;
      contract: {
        contract_ticker: string;
        strike_price: number;
        expiration_date: string;
        expiration_display: string;
        days_to_expiration: number;
        delta: number | null;
        bid: number | null;       // null until a quote feed exists
        ask: number | null;
        est_bid: number;          // estimated bid (P1-E1): the suggested sell limit, shown "est."
        est_ask: number | null;
        open_interest: number;
        implied_volatility: number | null;
        net_credit: number;       // est_bid minus cost_to_close
        premium: number;          // the option mark (last trade)
        spread_load: number | null;
        aroc: number;
        downside_protection: number;
        viability_warning: string | null;
      } | null;
      unavailable_reason?: string;
    }>;
    message?: string;
  }> {
    const response = await this.api.get(`/api/options/roll-strategies/${symbol}`, {
      params: {
        current_expiry: currentExpiry,
        cost_to_close: costToClose,
        contract_type: contractType
      }
    });
    return response.data;
  }

  // ========================================
  // Filter Presets API
  // ========================================

  /**
   * Get all presets (system + user's custom presets).
   */
  async getPresets(): Promise<{
    status: string;
    presets: Array<{
      id: number;
      user_id?: number;
      name: string;
      description?: string;
      is_system: boolean;
      system_key?: string;
      screener_filters: {
        iv_rank_min?: number;
        iv_rank_max?: number;
        annualized_yield_min?: number;
        annualized_yield_max?: number;
        spread_pct_min?: number;
        spread_pct_max?: number;
        market_cap_categories?: string[];
        sectors?: string[];
        exclude_sectors?: string[];
        net_margin_min?: number;
        net_margin_max?: number;
        debt_equity_max?: number;
        dividend_yield_min?: number;
        dividend_yield_max?: number;
        earnings_within_days?: number;
        exclude_earnings_within_days?: number;
        rsi_min?: number;
        rsi_max?: number;
        trend_signals?: string[];
      };
      contract_filters: {
        dte_min?: number;
        dte_max?: number;
        delta_min?: number;
        delta_max?: number;
        aroc_min?: number;
        aroc_max?: number;
        contract_iv_min?: number;
        contract_iv_max?: number;
        contract_spread_max?: number;
        open_interest_min?: number;
        theta_min?: number;
        theta_max?: number;
        gamma_min?: number;
        gamma_max?: number;
        vega_min?: number;
        vega_max?: number;
      };
      created_at: string;
      updated_at: string;
    }>;
    total: number;
  }> {
    const response = await this.api.get('/api/presets');
    return response.data;
  }

  /**
   * Create a new custom preset.
   */
  async createPreset(preset: {
    name: string;
    description?: string;
    [key: string]: any;
  }): Promise<{
    id: number;
    name: string;
    description?: string;
    is_system: boolean;
    screener_filters: any;
    contract_filters: any;
    created_at: string;
    updated_at: string;
  }> {
    const response = await this.api.post('/api/presets', preset);
    return response.data;
  }

  /**
   * Update a custom preset.
   */
  async updatePreset(
    presetId: number,
    updates: {
      name?: string;
      description?: string;
      [key: string]: any;
    }
  ): Promise<{
    id: number;
    name: string;
    description?: string;
    screener_filters: any;
    contract_filters: any;
    updated_at: string;
  }> {
    const response = await this.api.put(`/api/presets/${presetId}`, updates);
    return response.data;
  }

  /**
   * Delete a custom preset.
   */
  async deletePreset(presetId: number): Promise<void> {
    await this.api.delete(`/api/presets/${presetId}`);
  }

  // ========================================
  // Admin Logs API
  // ========================================

  /**
   * Get admin logs with optional filtering.
   * Requires X-Internal-Key header.
   */
  async getAdminLogs(filters: AdminLogFilters = {}): Promise<AdminLogsResponse> {
    const params = new URLSearchParams();

    if (filters.job_type) params.append('job_type', filters.job_type);
    if (filters.job_category) params.append('job_category', filters.job_category);
    if (filters.status) params.append('status', filters.status);
    if (filters.hours != null) params.append('hours', filters.hours.toString());
    if (filters.limit != null) params.append('limit', filters.limit.toString());
    if (filters.offset != null) params.append('offset', filters.offset.toString());

    const internalKey = process.env.REACT_APP_INTERNAL_KEY;
    const response = await this.api.get(`/api/admin/logs?${params.toString()}`, {
      headers: internalKey ? { 'X-Internal-Key': internalKey } : {}
    });
    return response.data;
  }

  /**
   * Get details of a specific log entry.
   */
  async getAdminLogDetails(id: number): Promise<CronJobLog> {
    const internalKey = process.env.REACT_APP_INTERNAL_KEY;
    const response = await this.api.get(`/api/admin/logs/${id}`, {
      headers: internalKey ? { 'X-Internal-Key': internalKey } : {}
    });
    return response.data;
  }

  /**
   * Get aggregated statistics for cron job executions.
   */
  async getAdminLogStats(hours: number = 24): Promise<{ status: string; statistics: CronJobStats }> {
    const internalKey = process.env.REACT_APP_INTERNAL_KEY;
    const response = await this.api.get(`/api/admin/logs/stats/summary?hours=${hours}`, {
      headers: internalKey ? { 'X-Internal-Key': internalKey } : {}
    });
    return response.data;
  }

  /**
   * Get recent failure logs.
   */
  async getAdminLogFailures(hours: number = 24, limit: number = 10): Promise<{ status: string; failures: CronJobLog[] }> {
    const internalKey = process.env.REACT_APP_INTERNAL_KEY;
    const response = await this.api.get(`/api/admin/logs/failures/recent?hours=${hours}&limit=${limit}`, {
      headers: internalKey ? { 'X-Internal-Key': internalKey } : {}
    });
    return response.data;
  }

  /**
   * Get available job types for filtering.
   */
  async getJobTypes(): Promise<{ status: string; job_types: JobTypeInfo[] }> {
    const internalKey = process.env.REACT_APP_INTERNAL_KEY;
    const response = await this.api.get('/api/admin/logs/job-types/list', {
      headers: internalKey ? { 'X-Internal-Key': internalKey } : {}
    });
    return response.data;
  }

  /**
   * Get recent batches with aggregated statistics.
   */
  async getRecentBatches(hours: number = 24, limit: number = 20): Promise<{ status: string; batches: CronBatchSummary[] }> {
    const internalKey = process.env.REACT_APP_INTERNAL_KEY;
    const response = await this.api.get(`/api/admin/logs/batches/recent?hours=${hours}&limit=${limit}`, {
      headers: internalKey ? { 'X-Internal-Key': internalKey } : {}
    });
    return response.data;
  }

  /**
   * Get details for a specific batch.
   */
  async getBatchDetails(batchId: string): Promise<{
    status: string;
    batch_id: string;
    batch_name: string | null;
    summary: {
      total_jobs: number;
      success_count: number;
      failure_count: number;
      total_duration_seconds: number;
      started_at: string | null;
      completed_at: string | null;
    };
    by_category: Record<string, CronJobLog[]>;
    jobs: CronJobLog[];
  }> {
    const internalKey = process.env.REACT_APP_INTERNAL_KEY;
    const response = await this.api.get(`/api/admin/logs/batches/${encodeURIComponent(batchId)}`, {
      headers: internalKey ? { 'X-Internal-Key': internalKey } : {}
    });
    return response.data;
  }

  /**
   * Founder-only coach funnel metrics (Reddit ad → page → chat → email → sub).
   * Internal-key gated, same convention as the admin-logs endpoints.
   */
  async getCoachMetrics(days: number = 7): Promise<CoachMetrics> {
    const internalKey = process.env.REACT_APP_INTERNAL_KEY;
    const response = await this.api.get(`/api/admin/coach/metrics?days=${days}`, {
      headers: internalKey ? { 'X-Internal-Key': internalKey } : {}
    });
    return response.data;
  }

  async getDistributionMetrics(days: number = 30): Promise<DistributionMetrics> {
    const internalKey = process.env.REACT_APP_INTERNAL_KEY;
    const response = await this.api.get(`/api/admin/distribution/metrics?days=${days}`, {
      headers: internalKey ? { 'X-Internal-Key': internalKey } : {}
    });
    return response.data;
  }

  async getPortfolioComposition(strategy?: 'all' | 'cc' | 'csp'): Promise<CompositionData> {
    // CC/CSP portfolio lens — omit for 'all' so the backend returns the whole book.
    const strategyParam = strategy && strategy !== 'all' ? `?strategy=${strategy}` : '';
    const response = await this.api.get(`/api/portfolio/composition${strategyParam}`);
    return response.data;
  }

  // ─── Dev Combo Analytics (dev-only page) ───────────────────────────────
  async getComboAnalyticsMeta(): Promise<{
    symbols: string[];
    baselines: Record<string, { baseline_pnl: number; baseline_win: number; n_total: number }>;
    factors: string[];
    highlightable_factors: string[];
    date_range: { start: string; end: string };
  }> {
    const response = await this.api.get('/api/dev/combo-analytics/meta');
    return response.data;
  }

  async getComboAnalyticsSetups(params: {
    symbol: string;
    top?: number;
    bottom?: number;
    factor_count?: number;
    min_samples?: number;
  }): Promise<{
    symbol: string;
    baseline_pnl: number;
    baseline_win: number;
    n_total: number;
    top: Array<{
      combo: string;
      factor_count: number;
      factor_a: string | null;
      factor_b: string | null;
      factor_c: string | null;
      n_samples: number;
      avg_pnl_pct: number;
      win_rate_pct: number;
      delta_pnl: number;
      delta_win: number;
      synergy: number | null;
    }>;
    bottom: Array<{
      combo: string;
      factor_count: number;
      factor_a: string | null;
      factor_b: string | null;
      factor_c: string | null;
      n_samples: number;
      avg_pnl_pct: number;
      win_rate_pct: number;
      delta_pnl: number;
      delta_win: number;
      synergy: number | null;
    }>;
  }> {
    const qs = new URLSearchParams({
      symbol: params.symbol,
      top: String(params.top ?? 10),
      bottom: String(params.bottom ?? 10),
      factor_count: String(params.factor_count ?? 2),
      min_samples: String(params.min_samples ?? 30),
    });
    const response = await this.api.get(`/api/dev/combo-analytics/setups?${qs}`);
    return response.data;
  }

  async getComboAnalyticsActiveDates(symbol: string, combo: string): Promise<{
    symbol: string;
    combo: string;
    supported: boolean;
    unsupported_factors?: string[];
    n_active?: number;
    active_dates: string[];
  }> {
    const qs = new URLSearchParams({ symbol, combo });
    const response = await this.api.get(`/api/dev/combo-analytics/active-dates?${qs}`);
    return response.data;
  }

  async getComboAnalyticsPriceHistory(symbol: string): Promise<{
    symbol: string;
    bars: Array<{ date: string; close: number }>;
  }> {
    const response = await this.api.get(`/api/dev/combo-analytics/price-history?symbol=${symbol}`);
    return response.data;
  }

  async getComboAnalyticsIndicatorBars(symbol: string): Promise<{
    symbol: string;
    n_bars: number;
    terciles: Record<string, [number, number]>;
    bars: Array<{
      date: string;
      close: number;
      hv_20: number;
      slope_12w: number;
      consol_z: number;
      cell_id: string;
      pnl: number | null;
    }>;
  }> {
    const response = await this.api.get(`/api/dev/combo-analytics/indicator-bars/${symbol}`);
    return response.data;
  }

  async getComboAnalyticsFingerprint(): Promise<{
    features: string[];
    universal_cuts: Record<string, [number, number]>;
    hv_bins: string[];
    slope_bins: string[];
    consol_bins: string[];
    all_cell_ids: string[];
    thresholds: { min_cell_n: number; win_delta_threshold_pp: number };
    stocks: Record<string, {
      baseline_pnl: number;
      baseline_win: number;
      n_total: number;
      n_trusted_cells: number;
      coverage_pct: number;
      cells: Record<string, {
        n: number;
        avg_pnl: number | null;
        win_rate: number | null;
        delta_pnl: number | null;
        delta_win: number | null;
        label: '+' | '-' | '0' | '?';
      }>;
      current_cell: string | null;
      fingerprint: string;
    }>;
    clustering: {
      n_clusters: number;
      assignments: Record<string, number>;
      summaries: Array<{
        cluster_id: number;
        members: string[];
        size: number;
        consensus_fingerprint: Record<string, string>;
      }>;
      distances: Record<string, Record<string, number>>;
    };
  }> {
    const response = await this.api.get('/api/dev/combo-analytics/fingerprint');
    return response.data;
  }

  async getComboAnalyticsRegimeGbr(): Promise<{
    features: string[];
    tree_features: string[];
    gbr_params: Record<string, any>;
    n_folds: number;
    stocks: Record<string, {
      n_total: number;
      baseline_pnl: number;
      baseline_win: number;
      grid_cv_r2_mean: number;
      grid_cv_r2_std: number;
      tree_cv_r2_mean: number;
      tree_cv_r2_std: number;
      gbr_cv_r2_mean: number;
      gbr_cv_r2_std: number;
      feature_importance: Record<string, number>;
      current_prediction_pct: number | null;
      current_features: Record<string, number> | null;
      current_delta_vs_baseline: number | null;
    }>;
    aggregate: {
      grid_mean_r2: number;
      tree_mean_r2: number;
      gbr_mean_r2: number;
      gbr_vs_tree_lift: number;
      gbr_vs_grid_lift: number;
      n_stocks: number;
    };
  }> {
    const response = await this.api.get('/api/dev/combo-analytics/regime-gbr');
    return response.data;
  }

  async getComboAnalyticsRegimeTree(): Promise<{
    features: string[];
    depth: number;
    stocks: Record<string, {
      baseline_pnl: number;
      baseline_win: number;
      n_total: number;
      feature_importance: Record<string, number>;
      leaves: Array<{
        leaf_id: number;
        rule: string;
        rule_parts: Array<{ feature: string; op: string; threshold: number }>;
        n_samples: number;
        avg_pnl_pct: number;
        win_rate_pct: number;
        delta_pnl: number;
        delta_win: number;
      }>;
      current_leaf_id: number | null;
    }>;
  }> {
    const response = await this.api.get('/api/dev/combo-analytics/regime-tree');
    return response.data;
  }

  async getComboAnalyticsRegimes(): Promise<{
    regime_order: string[];
    definitions: Record<string, { label: string; rule: string; description: string }>;
    rows: Array<{
      symbol: string;
      baseline_pnl: number;
      baseline_win: number;
      n_total: number;
      current_regime: string | null;
      cells: Record<string, {
        n_samples: number;
        avg_pnl_pct: number;
        win_rate_pct: number;
        delta_pnl: number;
        delta_win: number;
      } | null>;
    }>;
  }> {
    const response = await this.api.get('/api/dev/combo-analytics/regimes');
    return response.data;
  }

  async getComboAnalyticsCurrentSetup(symbol: string): Promise<{
    symbol: string;
    as_of_date: string;
    active_factors: string[];
    baseline_pnl: number | null;
    baseline_win: number | null;
    n_total: number | null;
    singles: Array<{
      combo: string;
      factor_count: number;
      factor_a: string | null;
      factor_b: string | null;
      factor_c: string | null;
      n_samples: number;
      avg_pnl_pct: number;
      win_rate_pct: number;
      delta_pnl: number;
      delta_win: number;
      synergy: number | null;
    }>;
    pairs: Array<{
      combo: string;
      factor_count: number;
      factor_a: string | null;
      factor_b: string | null;
      factor_c: string | null;
      n_samples: number;
      avg_pnl_pct: number;
      win_rate_pct: number;
      delta_pnl: number;
      delta_win: number;
      synergy: number | null;
    }>;
  }> {
    const response = await this.api.get(`/api/dev/combo-analytics/current-setup?symbol=${symbol}`);
    return response.data;
  }

  async getComboAnalyticsComputeCombo(symbol: string, factors: string[]): Promise<{
    symbol: string;
    factors: string[];
    supported: boolean;
    unsupported_factors?: string[];
    n_samples: number;
    avg_pnl_pct: number | null;
    win_rate_pct: number | null;
    delta_pnl: number | null;
    delta_win: number | null;
    baseline_pnl: number | null;
    baseline_win: number | null;
    baseline_n?: number;
    active_dates: string[];
  }> {
    const qs = new URLSearchParams({ symbol, factors: factors.join(',') });
    const response = await this.api.get(`/api/dev/combo-analytics/compute-combo?${qs}`);
    return response.data;
  }

  async getComboAnalyticsMatrix(params: {
    factor_count?: number;
    min_samples?: number;
    top_per_stock?: number;
    n_columns?: number;
  } = {}): Promise<{
    factor_count: number;
    min_samples: number;
    top_per_stock: number;
    top_columns: Array<{ combo: string; appearances: number }>;
    bottom_columns: Array<{ combo: string; appearances: number }>;
    rows: Array<{
      symbol: string;
      baseline_pnl: number;
      cells: Record<string, {
        delta_pnl: number;
        n_samples: number;
        avg_pnl_pct: number;
        win_rate_pct: number;
      } | null>;
    }>;
  }> {
    const qs = new URLSearchParams({
      factor_count: String(params.factor_count ?? 2),
      min_samples: String(params.min_samples ?? 30),
      top_per_stock: String(params.top_per_stock ?? 10),
      n_columns: String(params.n_columns ?? 5),
    });
    const response = await this.api.get(`/api/dev/combo-analytics/matrix?${qs}`);
    return response.data;
  }

  // ========================================
  // Frameworks API
  // ========================================

  /**
   * Get the indicator catalog for the framework builder.
   */
  async getIndicatorCatalog(): Promise<any> {
    const response = await this.api.get('/api/frameworks/catalog');
    return response.data;
  }

  /**
   * List user's frameworks + system defaults.
   */
  async getFrameworks(frameworkType?: string): Promise<any> {
    const params: any = {};
    if (frameworkType) params.framework_type = frameworkType;
    const response = await this.api.get('/api/frameworks', { params });
    return response.data;
  }

  /**
   * Create a new framework.
   */
  async createFramework(data: any): Promise<any> {
    const response = await this.api.post('/api/frameworks', data);
    return response.data;
  }

  /**
   * Update an existing framework.
   */
  async updateFramework(frameworkId: number, data: any): Promise<any> {
    const response = await this.api.put(`/api/frameworks/${frameworkId}`, data);
    return response.data;
  }

  /**
   * Delete a framework.
   */
  async deleteFramework(frameworkId: number): Promise<void> {
    await this.api.delete(`/api/frameworks/${frameworkId}`);
  }

  /**
   * Activate a framework (set as active for its type).
   */
  async activateFramework(frameworkId: number): Promise<any> {
    const response = await this.api.post(`/api/frameworks/${frameworkId}/activate`);
    return response.data;
  }

  /**
   * Deactivate a framework (revert to system default).
   */
  async deactivateFramework(frameworkId: number): Promise<any> {
    const response = await this.api.post(`/api/frameworks/${frameworkId}/deactivate`);
    return response.data;
  }

  /**
   * Clone a framework (system or public).
   */
  async cloneFramework(frameworkId: number, name?: string): Promise<any> {
    const params: any = {};
    if (name) params.name = name;
    const response = await this.api.post(`/api/frameworks/${frameworkId}/clone`, null, { params });
    return response.data;
  }

}

export const apiService = new ApiService();